<template>
    <a-modal
        v-model:open="modalOpen"
        title="Upload em massa"
        footer=""
        width="90%"
        style="max-width: 1100px; top: 4px"
        :bodyStyle="{ height: 'calc(100vh - 90px)', overflowY: 'auto' }"
        @cancel="onCancel"
    >
        <a-alert type="info" show-icon class="bulk-instructions">
            <template #message>Convenções de nomenclatura</template>
            <template #description>
                <ul class="bulk-instructions-list">
                    <li>Os arquivos devem conter o <strong>número de matrícula</strong> no nome (ex.: <code>12345.jpg</code>, <code>foto_12345.png</code>).</li>
                    <li>Em ZIP, use <strong>pastas</strong> para indicar o tipo: <code>lotes/</code> ou <code>lote/</code> (lote); <code>edificacoes/</code>, <code>edificacao/</code> ou <code>ed/</code> (edificação).</li>
                    <li>Em arquivos soltos, use <strong>prefixo ou sufixo</strong> (ex.: <code>lote_12345.jpg</code>, <code>12345_edificacao.pdf</code>).</li>
                    <li>Se nenhuma convenção for detectada, será usada a <strong>camada selecionada</strong> abaixo.</li>
                </ul>
            </template>
        </a-alert>

        <div class="modal-top-controls">
            <div class="modal-controls-stack">
                <label class="control-label">
                    Camada:
                    <a-select
                        :key="selectedLayerId"
                        v-model:value="selectedLayerId"
                        show-search
                        placeholder="Selecione a camada"
                        class="control-select-wide"
                        :options="layersAvailable"
                        :filter-option="filterOption"
                        :disabled="savingPending"
                        @change="handleLayerChange"
                    />
                </label>
                <div class="control-row-upload">
                    <label class="control-label">
                        Tipo de envio:
                        <a-select
                            v-model:value="uploadMode"
                            :disabled="savingPending"
                            class="control-select"
                            :options="uploadModeOptions"
                        />
                    </label>
                    <a-upload-dragger
                        name="file"
                        :multiple="draggerMultiple"
                        :show-upload-list="false"
                        :before-upload="beforeUpload"
                        :accept="draggerAccept"
                        :disabled="savingPending"
                        class="modal-upload-dragger-inline"
                    >
                        <div class="modal-upload-dragger-content">
                            <PlusOutlined />
                            <span>{{ draggerHint }}</span>
                        </div>
                    </a-upload-dragger>
                </div>
            </div>
        </div>

        <a-divider style="margin: 14px 0 10px 0" />

        <div v-if="pendingUploads.length" class="pending-uploads-block">
            <div class="pending-uploads-header">
                <span style="font-weight:bold">Pré-visualização ({{ pendingUploads.length }})</span>
                <a-button
                    type="primary"
                    :loading="savingPending"
                    :disabled="!canSubmit"
                    @click="submitBulkUpload"
                >
                    Enviar
                </a-button>
            </div>
            <div class="pending-uploads-grid">
                <div v-for="(item, idx) in pendingUploads" :key="item.uid" class="pending-upload-card">
                    <a-button
                        type="text"
                        danger
                        size="small"
                        class="pending-upload-remove"
                        :disabled="savingPending"
                        @click="removePendingUpload(idx)"
                    >
                        <DeleteOutlined />
                    </a-button>
                    <template v-if="isImagePath(item.name)">
                        <img :src="item.previewUrl" :alt="item.name" class="pending-upload-preview-img" />
                    </template>
                    <template v-else-if="isPdfPath(item.name)">
                        <embed :src="item.previewUrl" type="application/pdf" class="pending-upload-preview-pdf" />
                    </template>
                    <template v-else>
                        <div class="pending-upload-file-placeholder">
                            <i class="fas fa-file-alt"></i>
                            <span>{{ archiveTypeLabel(item.name) }}</span>
                        </div>
                    </template>
                    <div class="pending-upload-name">{{ item.name }}</div>
                </div>
            </div>
        </div>

        <div v-if="importReport" class="import-report-block">
            <div class="import-report-header">
                <span style="font-weight:bold">Relatório de importação</span>
                <a-space>
                    <a-tag
                        color="success"
                        class="report-filter-tag"
                        :class="{ 'report-filter-tag--active': reportStatusFilter === 'success' }"
                        @click="toggleReportFilter('success')"
                    >
                        {{ importReport.success }} importado(s)
                    </a-tag>
                    <a-tag
                        v-if="importReport.failed > 0"
                        color="error"
                        class="report-filter-tag"
                        :class="{ 'report-filter-tag--active': reportStatusFilter === 'failed' }"
                        @click="toggleReportFilter('failed')"
                    >
                        {{ importReport.failed }} falha(s)
                    </a-tag>
                    <a-button size="small" @click="copyReport">Copiar relatório</a-button>
                </a-space>
            </div>
            <a-table
                :columns="reportColumns"
                :data-source="filteredReportItems"
                :pagination="filteredReportItems.length > 10 ? { pageSize: 10 } : false"
                size="small"
                :row-key="reportRowKey"
            >
                <template #bodyCell="{ column, record }">
                    <template v-if="column.key === 'status'">
                        <a-tag :color="record.status === 'success' ? 'success' : 'error'">
                            {{ record.status === 'success' ? 'Sucesso' : 'Falha' }}
                        </a-tag>
                    </template>
                    <template v-else-if="column.key === 'reason'">
                        <span
                            v-if="record.reason"
                            :class="{ 'report-reason-highlight': isHighlightedReason(record.reason) }"
                        >
                            {{ reasonLabel(record.reason) }}
                        </span>
                        <span v-else>—</span>
                    </template>
                </template>
            </a-table>
        </div>
    </a-modal>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons-vue'
import { openNotification } from '../../../../utils/utilities'
import {
    getAcceptForBulk,
    getAcceptForCompressed,
    archiveTypeLabel,
    createPendingUploadItem,
    isImagePath,
    isPdfPath,
    isCompressedPath,
    revokePreviewUrls,
    validateBulkFileMime,
    validateCompressedFile,
    validateFileSize,
    MAX_BULK_FILE_COUNT,
} from '../utils/layerFileHelpers'

const props = defineProps({
    layersAvailable: {
        type: Array,
        default: () => [],
    },
})

const emit = defineEmits(['reloadFiles'])

const BULK_ENDPOINT = '/api/map-actions/layers/file/bulk'

const REASON_LABELS = {
    nome_fora_padrao: 'Nome fora do padrão (matrícula não identificada)',
    matricula_nao_encontrada: 'Matrícula não encontrada no cadastro',
    tipo_arquivo_invalido: 'Tipo de arquivo inválido',
    camada_nao_configurada: 'Camada não configurada',
    erro_armazenamento: 'Erro ao armazenar arquivo',
}

const HIGHLIGHTED_REASONS = new Set(['nome_fora_padrao', 'matricula_nao_encontrada'])

const uploadModeOptions = [
    { value: 'files', label: 'Arquivos' },
    { value: 'archive', label: 'Compactados' },
]

const reportColumns = [
    { title: 'Arquivo', dataIndex: 'file', key: 'file', ellipsis: true },
    { title: 'Status', key: 'status', width: 100 },
    { title: 'Matrícula', dataIndex: 'matricula', key: 'matricula', width: 100 },
    { title: 'Motivo', key: 'reason', ellipsis: true },
]

const modalOpen = ref(false)
const uploadMode = ref('files')
const selectedLayer = ref(null)
const selectedLayerId = ref(null)
const pendingUploads = ref([])
const savingPending = ref(false)
const importReport = ref(null)
const reportStatusFilter = ref(null)

const filteredReportItems = computed(() => {
    const items = importReport.value?.items ?? []
    if (!reportStatusFilter.value) {
        return items
    }

    return items.filter(item => item.status === reportStatusFilter.value)
})

const draggerMultiple = computed(() => uploadMode.value === 'files')
const draggerAccept = computed(() => (
    uploadMode.value === 'archive' ? getAcceptForCompressed() : getAcceptForBulk()
))
const draggerHint = computed(() => (
    uploadMode.value === 'archive'
        ? 'Arraste ou clique para enviar um arquivo compactado (.zip ou .rar)'
        : 'Arraste ou clique para enviar arquivos'
))
const canSubmit = computed(() => !!selectedLayer.value && pendingUploads.value.length > 0)

watch(
    () => props.layersAvailable,
    () => resetLayerSelection(),
    { deep: true }
)

watch(uploadMode, () => {
    clearPendingUploads()
})

function resetLayerSelection() {
    const layers = props.layersAvailable ?? []
    if (!layers.length) {
        selectedLayer.value = null
        selectedLayerId.value = null
        return
    }

    selectedLayer.value = layers[0]
    selectedLayerId.value = layers[0].value
}

function clearPendingUploads() {
    revokePreviewUrls(pendingUploads.value)
    pendingUploads.value = []
}

function resetState() {
    clearPendingUploads()
    importReport.value = null
    reportStatusFilter.value = null
    uploadMode.value = 'files'
    selectedLayer.value = null
    selectedLayerId.value = null
}

const openBulkUpload = () => {
    importReport.value = null
    reportStatusFilter.value = null
    uploadMode.value = 'files'
    clearPendingUploads()
    if (!selectedLayer.value && props.layersAvailable.length > 0) {
        resetLayerSelection()
    }
    modalOpen.value = true
}

const handleLayerChange = (layerId) => {
    selectedLayer.value = props.layersAvailable.find(layer => layer.value === layerId) ?? null
}

const filterOption = (input, option) => option.value.toLowerCase().indexOf(input.toLowerCase()) >= 0

const removePendingUpload = (index) => {
    const item = pendingUploads.value[index]
    if (item?.previewUrl) {
        URL.revokeObjectURL(item.previewUrl)
    }
    pendingUploads.value.splice(index, 1)
}

const beforeUpload = (file) => {
    const raw = file.originFileObj ?? file
    const mime = raw.type || file.type

    if (uploadMode.value === 'archive') {
        const compressedCheck = validateCompressedFile(raw)
        if (!compressedCheck.ok) {
            openNotification('error', compressedCheck.message)
            return false
        }
        clearPendingUploads()
        pendingUploads.value = [createPendingUploadItem(raw)]
        return false
    }

    if (isCompressedPath(raw.name || '')) {
        openNotification('error', 'Arquivos compactados não são permitidos neste modo. Use o tipo de envio Compactados.')
        return false
    }

    const mimeCheck = validateBulkFileMime(mime, raw.name || '')
    if (!mimeCheck.ok) {
        openNotification('error', mimeCheck.message)
        return false
    }

    const sizeCheck = validateFileSize(raw)
    if (!sizeCheck.ok) {
        openNotification('error', sizeCheck.message)
        return false
    }

    if (pendingUploads.value.length >= MAX_BULK_FILE_COUNT) {
        openNotification('error', `É permitido enviar no máximo ${MAX_BULK_FILE_COUNT} arquivos por lote.`)
        return false
    }

    pendingUploads.value.push(createPendingUploadItem(raw))
    return false
}

function buildFormData() {
    const formData = new FormData()
    formData.append('upload_mode', uploadMode.value)
    formData.append('layer_id', String(selectedLayer.value.value))
    formData.append('layer_type', selectedLayer.value.geoDocumentLayerType ?? 'geoserver')

    if (uploadMode.value === 'files') {
        pendingUploads.value.forEach((item) => {
            formData.append('files[]', item.raw)
        })
    } else {
        formData.append('archive', pendingUploads.value[0].raw)
    }

    return formData
}

function applyImportResult(result, message) {
    reportStatusFilter.value = null
    importReport.value = {
        total: result.total ?? 0,
        success: result.success ?? 0,
        failed: result.failed ?? 0,
        items: result.items ?? [],
    }

    const { success, failed, total } = importReport.value

    if (success > 0) {
        openNotification('success', message || `Processamento concluído: ${success} de ${total} arquivo(s) importado(s).`)
        clearPendingUploads()
        reloadFiles()
    } else if (failed > 0) {
        openNotification('error', message || 'Nenhum arquivo foi importado. Verifique o relatório.')
    }
}

const submitBulkUpload = async () => {
    if (!canSubmit.value) {
        return
    }

    savingPending.value = true
    importReport.value = null
    reportStatusFilter.value = null

    try {
        const { data } = await window.axios.post(BULK_ENDPOINT, buildFormData(), {
            headers: { 'Content-Type': 'multipart/form-data' },
        })

        if (data?.error) {
            openNotification('error', data.message || 'Erro ao processar upload em massa.')
            return
        }

        applyImportResult(data?.data ?? {}, data?.message)
    } catch (error) {
        const status = error?.response?.status
        const responseMessage = error?.response?.data?.message

        if (status === 413) {
            openNotification(
                'error',
                'Arquivo(s) muito grande(s) para o servidor. Reduza o tamanho ou envie menos arquivos por vez.'
            )
            return
        }

        openNotification('error', responseMessage || 'Erro ao enviar arquivos. Verifique se o serviço está disponível.')
    } finally {
        savingPending.value = false
    }
}

function reasonLabel(reason) {
    return REASON_LABELS[reason] ?? reason
}

function isHighlightedReason(reason) {
    return HIGHLIGHTED_REASONS.has(reason)
}

function toggleReportFilter(status) {
    reportStatusFilter.value = reportStatusFilter.value === status ? null : status
}

function reportRowKey(record, index) {
    return `${record.file}-${record.status}-${index}`
}

function formatReportText(report) {
    if (!report?.items?.length) {
        return `Total: ${report?.total ?? 0} | Sucesso: ${report?.success ?? 0} | Falhas: ${report?.failed ?? 0}`
    }

    const header = `Relatório de importação — ${report.success}/${report.total} importado(s), ${report.failed} falha(s)\n`
    const lines = report.items.map((item) => {
        const parts = [item.file, item.status === 'success' ? 'OK' : 'FALHA']
        if (item.matricula != null) parts.push(`matrícula: ${item.matricula}`)
        if (item.reason) parts.push(reasonLabel(item.reason))
        return parts.join(' | ')
    })

    return header + lines.join('\n')
}

const copyReport = async () => {
    if (!importReport.value) {
        return
    }

    try {
        await navigator.clipboard.writeText(formatReportText(importReport.value))
        openNotification('success', 'Relatório copiado!')
    } catch {
        openNotification('error', 'Não foi possível copiar o relatório.')
    }
}

const onCancel = () => {
    resetState()
}

const reloadFiles = () => emit('reloadFiles')

defineExpose({
    openBulkUpload,
    reloadFiles,
})
</script>

<style scoped>
.bulk-instructions {
    margin-bottom: 14px;
}

.bulk-instructions-list {
    margin: 0;
    padding-left: 18px;
}

.bulk-instructions-list li {
    margin-bottom: 4px;
}

.modal-top-controls {
    margin-top: 8px;
}

.modal-controls-stack {
    display: flex;
    flex-direction: column;
    gap: 12px;
    max-width: 640px;
}

.control-row-upload {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
}

.control-label {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
}

.control-select {
    width: 140px;
}

.control-select-wide {
    width: 260px;
}

.modal-upload-dragger-inline {
    flex: 1;
    min-width: 220px;
    margin: 0;
}

.modal-upload-dragger-content {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    font-size: 12px;
    color: #555;
}

.pending-uploads-block {
    margin-bottom: 16px;
    padding: 12px;
    background: #fafafa;
    border: 1px dashed #d9d9d9;
    border-radius: 8px;
}

.pending-uploads-header,
.import-report-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 12px;
    flex-wrap: wrap;
}

.pending-uploads-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
}

.pending-upload-card {
    position: relative;
    width: 200px;
    min-height: 140px;
    padding: 8px;
    background: #fff;
    border-radius: 6px;
    border: 1px solid #eee;
}

.pending-upload-remove {
    position: absolute;
    top: 2px;
    right: 2px;
    z-index: 2;
}

.pending-upload-preview-img {
    max-width: 100%;
    max-height: 120px;
    object-fit: contain;
    display: block;
    margin: 0 auto;
}

.pending-upload-preview-pdf {
    width: 100%;
    height: 120px;
    border: none;
}

.pending-upload-file-placeholder {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 6px;
    min-height: 100px;
    color: #1677ff;
    font-size: 28px;
}

.pending-upload-name {
    margin-top: 6px;
    font-size: 11px;
    color: #666;
    word-break: break-all;
    text-align: center;
}

.import-report-block {
    margin-top: 16px;
    padding: 12px;
    background: #fafafa;
    border: 1px solid #eee;
    border-radius: 8px;
}

.report-reason-highlight {
    color: #cf1322;
    font-weight: 500;
}

.report-filter-tag {
    cursor: pointer;
    user-select: none;
    transition: box-shadow 0.15s ease, opacity 0.15s ease;
}

.report-filter-tag:hover {
    opacity: 0.85;
}

.report-filter-tag--active {
    box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.15);
    font-weight: 600;
}
</style>
